import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import Reveal from "@/components/anim/Reveal";

const ContactForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacts", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white section-y" data-testid="contact-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <p className="overline">{t.contact.eyebrow}</p>
          <h2 className="mt-4 font-heading font-bold text-brand-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">{t.contact.subtitle}</p>

          <div className="mt-12 space-y-5">
            <a href={`mailto:${t.contact.info_email}`} className="flex items-start gap-4 group" data-testid="contact-info-email-row">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-brand-border bg-brand-surface text-brand-deep transition-all duration-300 group-hover:bg-brand-deep group-hover:text-white group-hover:-translate-y-0.5">
                <Mail size={18} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-muted">Email</p>
                <span className="text-brand-ink font-medium group-hover:text-brand-deep transition-colors" data-testid="contact-info-email">
                  {t.contact.info_email}
                </span>
              </div>
            </a>
            <a href={`tel:${t.contact.info_phone_raw}`} className="flex items-start gap-4 group" data-testid="contact-info-phone-row">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-brand-border bg-brand-surface text-brand-deep transition-all duration-300 group-hover:bg-brand-deep group-hover:text-white group-hover:-translate-y-0.5">
                <Phone size={18} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-muted">{lang === "fr" ? "Téléphone" : "Phone"}</p>
                <span className="text-brand-ink font-medium group-hover:text-brand-deep transition-colors" data-testid="contact-info-phone">
                  {t.contact.info_phone}
                </span>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-brand-border bg-brand-surface text-brand-deep">
                <MapPin size={18} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-muted">{lang === "fr" ? "Localisation" : "Location"}</p>
                <p className="text-brand-ink font-medium">{t.contact.info_location}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center border border-brand-border bg-brand-surface text-brand-deep">
                <Clock size={18} />
              </span>
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-muted">{lang === "fr" ? "Horaires" : "Hours"}</p>
                <p className="text-brand-ink font-medium">{t.contact.info_hours}</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal as="form" delay={120}
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-brand-surface border border-brand-border p-8 sm:p-10 space-y-6 transition-shadow duration-500 hover:shadow-xl"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-semibold tracking-[0.12em] uppercase text-brand-ink/80">
                {t.contact.name} *
              </Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={onChange("name")}
                className="rounded-none bg-white border-brand-border h-12 focus-visible:ring-brand-deep"
                data-testid="contact-input-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold tracking-[0.12em] uppercase text-brand-ink/80">
                {t.contact.email} *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={onChange("email")}
                className="rounded-none bg-white border-brand-border h-12 focus-visible:ring-brand-deep"
                data-testid="contact-input-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="company" className="text-xs font-semibold tracking-[0.12em] uppercase text-brand-ink/80">
                {t.contact.company}
              </Label>
              <Input
                id="company"
                value={form.company}
                onChange={onChange("company")}
                className="rounded-none bg-white border-brand-border h-12 focus-visible:ring-brand-deep"
                data-testid="contact-input-company"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-xs font-semibold tracking-[0.12em] uppercase text-brand-ink/80">
                {t.contact.subject} *
              </Label>
              <Input
                id="subject"
                required
                value={form.subject}
                onChange={onChange("subject")}
                className="rounded-none bg-white border-brand-border h-12 focus-visible:ring-brand-deep"
                data-testid="contact-input-subject"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-xs font-semibold tracking-[0.12em] uppercase text-brand-ink/80">
              {t.contact.message} *
            </Label>
            <Textarea
              id="message"
              required
              rows={6}
              value={form.message}
              onChange={onChange("message")}
              className="rounded-none bg-white border-brand-border resize-none focus-visible:ring-brand-deep"
              data-testid="contact-input-message"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="rounded-none bg-brand-ink hover:bg-brand-deep text-white h-12 px-7 text-sm font-semibold tracking-wide w-full sm:w-auto group relative overflow-hidden transition-all duration-300 hover:px-9"
            data-testid="contact-submit-button"
          >
            <span className="relative z-10 inline-flex items-center">
              {loading ? t.contact.sending : <>{t.contact.send}<Send size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" /></>}
            </span>
          </Button>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
